import i18n from "src/locales";
import type { PaymentAddon } from "src/utils/redux/slices/paymentAddon";
import { getMeristemConfigValue } from "./meristemConfig";
import { isCheckoutOfferTimerExpired } from "@utils/checkoutTimer";
import {
  isDECoursePackEligible,
  shouldShowCoursePackCheckoutOffers,
  isEligibleForPersonalizedPlanTimer,
} from "./userSegment/features";
import { trackEvent } from "./api/tracker";
import getStore from "./redux/store";
import { TELEHEALTH_ADDON_PREFIX } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";

/* The utility functions in this file allow us to make changes to the course pack through the meristemConfig json
in order to enable "pseudo experiments" without consuming traffic. These pseudoexperiments can target any context in Meristem, and
the meristemConfig flags will propogate through the entire user journey to enable these course pack changes.

You will need to set up the meristemConfig to look something like this:

Control branch:

{
  "coursePackExperimentName": "exXXX",
  "coursePackExperimentVariation": "control"
}

Variation Branch:

{
  "coursePackExperimentName": "exXXX",
  "coursePackExperimentVariation": "variation",
  "coursePackProductId": "nw-plus-xxx", // New addon product id that will be used in the experiment
  "coursePackProductTitle": "Some title" // New addon product title that will be used in the experiment for contexts before payment-checkout where the add-on is not loaded yet
}

*/

const DEFAULT_PRODUCT_ID = "nw-plus-ex97-baseline";
const DEFAULT_CHANNEL_ID = "hw-course-add-ons-baseline";
const DEFAULT_CLINICAL_CHANNEL_ID = "noom-clinical-baseline";
export const FREE_COURSE_PACK_OFFER_PRODUCT_ID = "nw-plus-free-baseline";

export const getPaymentAddonOfferChannels = (): string[] => {
  return [DEFAULT_CHANNEL_ID, DEFAULT_CLINICAL_CHANNEL_ID];
};

export const getNoomClinicalProductId = () => {
  const { recommendedPlan } = getStore().getState();

  const planLength = recommendedPlan.duration_in_months;
  const trialDuration = recommendedPlan.trial_duration;

  const trialKeyMapping = {
    0: 0,
    7: 8,
    14: 15,
  };

  const trialKey = trialKeyMapping[trialDuration];
  if (trialKey === undefined) {
    trackEvent("NCInvalidBaseTrialDuration", { trialDuration });
    return "";
  }

  const planName = `${TELEHEALTH_ADDON_PREFIX}${planLength}m-${trialKey}t`;
  if (planName === `${TELEHEALTH_ADDON_PREFIX}1m-15t`) {
    return `${TELEHEALTH_ADDON_PREFIX}telex171-1m-15t`;
  }
  return planName;
};

/** Mental Wellness Coursepack */
export const getMWCoursepackId = () => {
  if (isDECoursePackEligible()) {
    return `${DEFAULT_PRODUCT_ID}-de`;
  }

  const productId: string =
    getMeristemConfigValue("coursePackProductId") ||
    FREE_COURSE_PACK_OFFER_PRODUCT_ID;

  if (productId === FREE_COURSE_PACK_OFFER_PRODUCT_ID) {
    return isCheckoutOfferTimerExpired()
      ? DEFAULT_PRODUCT_ID
      : FREE_COURSE_PACK_OFFER_PRODUCT_ID;
  }

  return productId;
};

/** Aging and Metabolism Coursepack */
export const getAMCoursepackId = () => "aging-and-metabolism-coursepack";

export const getPaymentAddonProduct = (paymentAddon?: PaymentAddon) => {
  const productId = paymentAddon?.addon_product_id;
  if (productId?.startsWith("nw-plus-")) {
    return "mentalWellnessCoursepack";
  }
  if (productId?.startsWith("aging-and-metabolism-")) {
    return "agingMetabolismCoursepack";
  }
  if (productId?.startsWith(TELEHEALTH_ADDON_PREFIX)) {
    return "noomClinical";
  }
  return null;
};

export type PaymentAddonProduct =
  | ReturnType<typeof getPaymentAddonProduct>
  | "kickstart";

export const getPaymentAddonTitle = (paymentAddon?: PaymentAddon) => {
  if (paymentAddon?.title) {
    return paymentAddon.title;
  }
  // Allow using a title from meristem config in case we need it earlier in the buyflow
  // when the paymentAddon is not loaded yet and we don't want to make an extra API call
  const coursePackProductTitle = getMeristemConfigValue(
    "coursePackProductTitle"
  );
  return coursePackProductTitle || i18n.t("price-breakdown:coursePackTitle");
};

// By default this is called from the checkout page, but if any changes are made earlier then the call to this function will need to be moved
export const maybeTrackCoursePackExperimentStarted = () => {
  const coursePackExperimentName = getMeristemConfigValue(
    "coursePackExperimentName"
  );

  const coursePackExperimentVariation = getMeristemConfigValue(
    "coursePackExperimentVariation"
  );

  if (
    coursePackExperimentName &&
    coursePackExperimentVariation &&
    shouldShowCoursePackCheckoutOffers()
  ) {
    trackEvent("CoursePackHoldoutExperimentStarted", {
      experimentName: coursePackExperimentName,
      experimentVariation: coursePackExperimentVariation,
    });
  }
};

export function getCoursePackTextPrefix(basePrefix) {
  if (isEligibleForPersonalizedPlanTimer()) {
    return basePrefix;
  }
  return `${basePrefix}WithNMCoursepack`;
}

/**
 * Not all paymentAddons should be shown to the user,
 * e.g. a Noom Med subscription that doesn't match the duration of the user's HW program
 * or the paid Mental Wellness coursepack when the user is eligble for the free one.
 */
export const isShowablePaymentAddon = (addon: PaymentAddon) =>
  addon.addon_product_id === getNoomClinicalProductId() ||
  addon.addon_product_id === getMWCoursepackId() ||
  addon.addon_product_id === getAMCoursepackId();

export const getTranslationKey = (
  product: NonNullable<PaymentAddonProduct>
) => {
  const translationKeyMapping: Record<
    NonNullable<PaymentAddonProduct>,
    string
  > = {
    mentalWellnessCoursepack: "mood",
    agingMetabolismCoursepack: "agingMetabolism",
    noomClinical: "clinical",
    kickstart: "kickstart",
  };
  return translationKeyMapping[product];
};
